// NPM Imports

// Helpers

// Internal imports
import MainNav from "./MainNav";
import Slider from "./Slider";
import Accordion from "./Accordion";
import LazyLoad from "./LazyLoad";
import TickerBoardJS from "./TickerBoardJS";
import TelephoneValidation from "./TelephoneValidation";
import jobSubmissionForm from "./jobSubmissionForm";
import Map from "./Map";
import TabbedContent from "./TabbedContent";
import Lightbox from "./Lightbox";
import GIFAnimation from "./GIFAnimation";
import Team from "./Team";
import CookieNotification from "./CookieNotification";

/**
 * Main App module used in index.js to initialise all other modules, includes helper
 * functions/modules for use in initialising modules depending on various criteria
 *
 * @returns {{init: init}}
 */
const app = (() => {
  const loadedModules = {};

  const alwaysInit = (Module, name = false, options = false) => {
    if (name) {
      loadedModules[name] = Module;
    }
    Module.init(options);
  };

  const initialiseIfPartialInPage = (
    partial,
    Module,
    name = false,
    options = false
  ) => {
    if (window.partialsUsed.includes(partial)) {
      Module.init(options);
      if (name) {
        loadedModules[name] = Module;
      }
    }
  };

  const initialiseIfClassInPage = (classInPage, Module) => {
    if (document.getElementsByClassName(classInPage).length > 0) {
      Module.init();
    }
  };

  /**
   * @param Module {Object}
   * @param Module.init {Function}
   * @param name {string|boolean}
   * @param options {Object}
   *
   * Detects preferred reduce motion before initiating the module
   */
  const initialiseIfAnimationAllowed = (
    Module,
    name = false,
    options = false
  ) => {
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      Module.init(options);
      if (name) {
        loadedModules[name] = Module;
      }
    }
  };

  /**
   * @param Module {Object}
   * @param Module.init {Function}
   * @param name {string|boolean}
   * @param CB {Function|Boolean}
   * @param options {Object}
   */
  const initialiseIfOnHomepage = (
    Module,
    name = false,
    CB = false,
    options = false
  ) => {
    if (window.isHomePage) {
      Module.init(options);
      if (name) {
        loadedModules[name] = Module;
      }
      if (CB !== false) {
        CB(true);
      }
    } else if (CB !== false) {
      CB(false);
    }
  };

  /**
   * @param Module {Object}
   * @param Module.init {Function}
   * @param name {string|boolean}
   * @param CB {Function|Boolean}
   * @param options {Object}
   */
  const initialiseIfNotOnHomepage = (
    Module,
    name = false,
    CB = false,
    options = false
  ) => {
    if (!window.isHomePage) {
      Module.init(options);
      if (name) {
        loadedModules[name] = Module;
      }
      if (CB !== false) {
        CB(true);
      }
    } else if (CB !== false) {
      CB(false);
    }
  };

  /**
   * @param MaxWidth
   *
   *      800 // MAX BREAKPOINT Or false if no max
   *
   * @param Module {Object}
   * @param Module.init {Function} - initialises the module
   *
   * Detects media query is reached before initiating the module, uses resize event listener to initialise the module
   * when this breakpoint is reached
   */
  const initialiseInBreakpoint = (MaxWidth, Module, options) => {
    const media = window.matchMedia("(max-width:" + MaxWidth + "px)");

    if (!media) {
      return false;
    }

    if (media.matches) {
      Module.init(options);
    } else {
      const initOnResize = () => {
        if (media.matches) {
          Module.init(options);
          window.removeEventListener("resize", initOnResize);
        }
      };
      window.addEventListener("resize", initOnResize);
    }
    return true;
  };

  const initialiseInBreakpointMin = (MinWidth, Module, options) => {
    const media = window.matchMedia("(min-width:" + MinWidth + "px)");

    if (!media) {
      return false;
    }

    if (media.matches) {
      Module.init(options);
    } else {
      const initOnResize = () => {
        if (media.matches) {
          Module.init(options);
          window.removeEventListener("resize", initOnResize);
        }
      };
      window.addEventListener("resize", initOnResize);
    }
    return true;
  };

  const initialiseIfTemplateEquals = (
    template,
    Module,
    name = false,
    options = false
  ) => {
    if (window.pageTemplate === template) {
      Module.init(options);
      if (name) {
        loadedModules[name] = Module;
      }
    }
  };

  const getLoadedModules = () => loadedModules;
  const getLoadedModule = (moduleName) => {
    if (typeof loadedModules[moduleName] !== "undefined") {
      return loadedModules[moduleName];
    }

    return false;
  };

  const init = () => {
    // Always init
    initialiseInBreakpoint("991", MainNav);
    alwaysInit(Slider);
    initialiseInBreakpoint("720", Accordion, {
      accordionClass: "accordion__panel--mobile",
      accordionTrigger: "accordion__panel__trigger--mobile",
      speed: "200",
      breakpoint: "720",
    });
    initialiseInBreakpoint("1200", Accordion, {
      accordionClass: "accordion__panel--tablet",
      accordionTrigger: "accordion__panel__trigger--tablet",
      speed: "200",
      breakpoint: "1200",
    });
    Accordion.init({
      accordionClass: "accordion__panel--default",
      accordionTrigger: "accordion__panel__trigger--default",
      speed: "200",
    });
    alwaysInit(LazyLoad);
    alwaysInit(TickerBoardJS);
    alwaysInit(TelephoneValidation);
    alwaysInit(jobSubmissionForm);
    alwaysInit(Map);
    alwaysInit(TabbedContent);
    alwaysInit(Lightbox);
    alwaysInit(GIFAnimation);
    alwaysInit(Team);
    alwaysInit(CookieNotification);

    // Sometimes init
  };

  return {
    init,
    // getLoadedModules,
    // getLoadedModule
  };
})();

export default app;
